import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import { Box, Stack } from "@mui/material";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import "../../../styles/analytics/report-modal.scss";
import CustomAnalytics from "./sections/custom-analytics.jsx";
import EditReport from "./sections/edit-report.jsx";
import AddTitle from "./sections/add-title.jsx";
import BootstrapButton from "../ui/BootstrapButton.jsx";
import { usePrevious } from "../../../utils/analytics-utils";
import FetchNewData from "./sections/fetch-new-data.jsx";

const EditAnalyticsReportContent = ({
  clientReport,
  saveReportConfiguration,
  savingReportConfiguration,
  onUpdateClientReport,
  isLoading,
  updateReportError,
}) => {
  const [title, setTitle] = useState("Loading report...");
  const [coverPageText, setCoverPageText] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [metricCheckList, setMetricCheckList] = useState([]);
  const [generateReportError, setGenerateReportError] = useState("");
  const [minimizeConfigureReport, setMinimizeConfigureReport] = useState("");
  const [savedSuccess, setSavedSuccess] = useState(false);
  const prevSavingReportConfiguration = usePrevious(savingReportConfiguration);

  const generateReport = () => {
    if (!clientReport.title) {
      setGenerateReportError("Please specify a title for this report");
      return;
    }

    saveReportConfiguration(clientReport);
  };

  useEffect(() => {
    if (clientReport.id) {
      const tempMetricCheckList = [];

      if (clientReport.showAdAnalytics) {
        tempMetricCheckList.push("showAdAnalytics");
      }
      if (clientReport.showWebAnalyticsTraffic) {
        tempMetricCheckList.push("showWebAnalyticsTraffic");
      }
      if (clientReport.showWebAnalyticsSocial) {
        tempMetricCheckList.push("showWebAnalyticsSocial");
      }
      if (clientReport.showWebAnalytics) {
        tempMetricCheckList.push("showWebAnalytics");
      }
      if (clientReport.showAccounts) tempMetricCheckList.push("showAccounts");
      if (clientReport.showInsights) tempMetricCheckList.push("showInsights");
      if (clientReport.showHashtagAnalytics)
        tempMetricCheckList.push("showHashtagAnalytics");
      if (clientReport.showCategoryAnalytics)
        tempMetricCheckList.push("showCategoryAnalytics");
      if (clientReport.showTopPosts)
        tempMetricCheckList.push("showTopPosts");

      if (clientReport.showOverviewFollowerData)
        tempMetricCheckList.push("showOverviewFollowerData");
      if (clientReport.showOverviewEngagementBreakdown) {
        tempMetricCheckList.push("showOverviewEngagementBreakdown");
      }
      if (clientReport.showOverviewEngagementRate) {
        tempMetricCheckList.push("showOverviewEngagementRate");
      }
      if (clientReport.showOverviewExposureData) {
        tempMetricCheckList.push("showOverviewExposureData");
      }
      if (clientReport.showOverviewPostPerformance) {
        tempMetricCheckList.push("showOverviewPostPerformance");
      }
      if (clientReport.campaigns) setCampaigns(clientReport.campaigns);

      setMetricCheckList([...tempMetricCheckList]);
      setCoverPageText(clientReport.coverPage);
      setTitle(clientReport.title);
    }
  }, [clientReport?.id]);

  useEffect(() => {
    if (clientReport?.id && !isLoading) {
      const newReportData = { ...clientReport };
      newReportData.title = title;
      newReportData.showAdAnalytics = !!metricCheckList.find(
        (x) => x === "showAdAnalytics"
      );
      newReportData.showWebAnalyticsTraffic = !!metricCheckList.find(
        (x) => x === "showWebAnalyticsTraffic"
      );
      newReportData.showWebAnalyticsSocial = !!metricCheckList.find(
        (x) => x === "showWebAnalyticsSocial"
      );
      newReportData.showWebAnalytics =
        newReportData.showWebAnalyticsTraffic ||
        newReportData.showWebAnalyticsSocial;
      newReportData.showAccounts = !!metricCheckList.find(
        (x) => x === "showAccounts"
      );
      newReportData.showInsights = !!metricCheckList.find(
        (x) => x === "showInsights"
      );
      newReportData.showHashtagAnalytics = !!metricCheckList.find(
        (x) => x === "showHashtagAnalytics"
      );
      newReportData.showCategoryAnalytics = !!metricCheckList.find(
        (x) => x === "showCategoryAnalytics"
      );
      newReportData.showTopPosts = !!metricCheckList.find(
          (x) => x === "showTopPosts"
      );


      newReportData.showOverviewFollowerData = !!metricCheckList.find(
        (x) => x === "showOverviewFollowerData"
      );
      newReportData.showOverviewEngagementBreakdown = !!metricCheckList.find(
        (x) => x === "showOverviewEngagementBreakdown"
      );
      newReportData.showOverviewEngagementRate = !!metricCheckList.find(
        (x) => x === "showOverviewEngagementRate"
      );
      newReportData.showOverviewExposureData = !!metricCheckList.find(
        (x) => x === "showOverviewExposureData"
      );
      newReportData.showOverviewPostPerformance = !!metricCheckList.find(
        (x) => x === "showOverviewPostPerformance"
      );

      newReportData.showOverview =
      newReportData.showOverviewEngagementData || 
      newReportData.showOverviewFollowerData || 
      newReportData.showOverviewEngagementRate || 
      newReportData.showOverviewExposureData || 
      newReportData.showOverviewEngagementBreakdown || 
      newReportData.showOverviewPostPerformance || 
      newReportData.showHashtagAnalytics || 
      newReportData.showCategoryAnalytics; 

      newReportData.showOrganicAnalytics =
        newReportData.showOverview ||
        newReportData.showAccount ||
        newReportData.showInsights ||
        newReportData.showHashtagAnalytics ||
        newReportData.showCategoryAnalytics ||
        newReportData.showTopPosts;

      newReportData.coverPage = coverPageText;
      newReportData.campaigns = campaigns;

      onUpdateClientReport({ ...newReportData });
    }
  }, [title, metricCheckList, coverPageText, campaigns, isLoading]);

  useEffect(() => {
    if (
      !updateReportError &&
      prevSavingReportConfiguration &&
      !savingReportConfiguration
    ) {
      setSavedSuccess(true);
      setTimeout(() => {
        setSavedSuccess(false);
      }, 2000);
    }
  }, [
    updateReportError,
    savingReportConfiguration,
    prevSavingReportConfiguration,
  ]);

  let nodes = [
    ...(clientReport.hasOrganicAnalytics
      ? [
          {
            value: "organic",
            label: "Organic Analytics",
            children: [
              {
                value: "overview",
                label: "Overview",
                children: [
                  {
                    value: "showOverviewFollowerData",
                    label: "Audience Growth",
                  },
                  { value: "showOverviewExposureData", label: "Exposure" },
                  {
                    value: "showOverviewEngagementBreakdown",
                    label: "Engagement Breakdown",
                  },
                  {
                    value: "showOverviewEngagementRate",
                    label: "Engagement Rate",
                  },
                  { value: "showOverviewPostPerformance", label: "Post Performance" },
                  {
                    value: "showHashtagAnalytics",
                    label: "Hashtag Analytics",
                  },
                  {
                    value: "showCategoryAnalytics",
                    label: "Content Tag Analytics",
                  },
                ],
              },
              { value: "showAccounts", label: "Accounts" },
              { value: "showInsights", label: "Insights" },
              {
                value: "showTopPosts",
                label: "Top Posts",
              },
            ],
          },
        ]
      : []),
    ...(clientReport.hasAdAnalytics
      ? [
          {
            value: "showAdAnalytics",
            label: "Ad Analytics",
          },
        ]
      : []),
    ...(clientReport.hasWebsiteAnalytics
      ? [
          {
            value: "showWebAnalytics",
            label: "Web Analytics",
            children: [
              { value: "showWebAnalyticsTraffic", label: "Traffic data" },
              {
                value: "showWebAnalyticsSocial",
                label: "Acquisition data",
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <Paper
      className="edit-modal"
      elevation={0}
      sx={{ boxShadow: "0 5px 15px rgba(0,0,0,.5)" }}
    >
      <Box className="edit-modal-header">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>Configure Report</Box>
          {minimizeConfigureReport ? (
            <span
              className="close"
              onClick={() => setMinimizeConfigureReport(false)}
            >
              <i className="fa fa-chevron-circle-right" aria-hidden="true"></i>
            </span>
          ) : (
            <span
              className="close"
              onClick={() => setMinimizeConfigureReport(true)}
            >
              <i className="fa fa-chevron-circle-left" aria-hidden="true"></i>
            </span>
          )}
        </Stack>
      </Box>
      {!minimizeConfigureReport && (
        <>
          <Box className="report-modal edit-modal-content">
            <Stack spacing="30px">
              <AddTitle title={title} setTitle={setTitle} label="Title" />

              {!isLoading && (
                <>
                  <EditReport
                    metricCheckList={metricCheckList}
                    setMetricCheckList={setMetricCheckList}
                    reportId={clientReport.id}
                    nodes={nodes}
                    expanded={["organic", "overview", "showWebAnalytics"]}
                  />

                  <FetchNewData
                    coverPageText={coverPageText}
                    setCoverPageText={setCoverPageText}
                    reportId={clientReport.id}
                  />

                  <CustomAnalytics
                    campaigns={campaigns}
                    setCampaigns={setCampaigns}
                  />
                </>
              )}
            </Stack>
          </Box>
          <Box className="edit-modal-footer">
            <Stack direction="column" alignItems="center">
              {generateReportError && (
                <p
                  id="feedback-error"
                  style={{ color: "red" }}
                  class="ng-binding"
                >
                  {generateReportError || updateReportError}
                </p>
              )}
              <Box>
                <BootstrapButton
                  className={`modal-button ${
                    savedSuccess ? "content-saved" : ""
                  }`}
                  disabled={savingReportConfiguration || isLoading}
                  onClick={generateReport}
                >
                  {savingReportConfiguration ? (
                    <>
                      <i
                        class="fa fa-spinner fa-pulse fa-fw"
                        style={{ marginRight: "5px" }}
                      ></i>
                      Saving...
                    </>
                  ) : savedSuccess ? (
                    "Saved!"
                  ) : (
                    "Save"
                  )}
                </BootstrapButton>
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default EditAnalyticsReportContent;
